import {
    Add,
    AlertModal,
    Box,
    Button,
    External,
    Modal,
    showToast,
    TabMenu,
    TabMenuItem,
    TextBlock,
    Upload,
    useSubPage,
} from "@bcc-ui/ui-components";
import { useFeature } from "flagged";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import style from "./asset-custom-view-page.scss";
import AssetViewKebabMenu from "components/asset-management-system/asset-view-kebab-menu/AssetViewKebabMenu";
import AssetTable from "components/asset-management-system/AssetTable";
import { ManifestTab } from "components/asset-management-system/custom-views/tabs/ManifestTab";
import { OverviewTab } from "components/asset-management-system/custom-views/tabs/overview/OverviewTab";
import { ReportsTab } from "components/asset-management-system/custom-views/tabs/ReportsTab";
import { RiskTab } from "components/asset-management-system/custom-views/tabs/risk/RiskTab";
import { ValueTab } from "components/asset-management-system/custom-views/tabs/value/ValueTab";
import ExportAssetsView from "components/asset-management-system/ExportAssetsView";
import { ManageAssetView } from "components/asset-management-system/manage-assets/ManageAssetView";
import { UploadAssetsModal } from "components/asset-management-system/upload/UploadAssetsModal";
import ApplicationLayout from "components/layout/ApplicationLayout";
import { ASSET_MANAGEMENT_ROUTE, ASSET_OVERVIEW_ROUTE } from "components/router/Routes";
import { RiskLevel } from "domain/asset-management/assetFilterConstants";
import {
    AssetCustomView,
    buildViewFilterLogic,
    mapCustomViewFiltersToApiFilters,
} from "domain/asset-management/assets";
import { buildCosmeticGradingUrl } from "domain/asset-management/cosmeticGrading";
import {
    AUTH_ASSET_CREATE,
    AUTH_ASSET_GRADING,
    AUTH_ASSET_MANIFEST_VIEW,
    AUTH_ASSET_VIEW,
    AUTH_CUSTOM_ASSET_VIEW_VIEW,
} from "domain/authority";
import { Breakpoint } from "domain/breakpoints";
import { assetViewService, Manifest } from "services/asset-management/AssetCustomViewService";
import {
    Asset,
    AssetFilter,
    AssetFilters,
    AssetSearchRequest,
    DEFAULT_COLUMN_KEYS,
} from "services/asset-management/AssetService";
import { FLAG_ASSET_MANAGEMENT, FLAG_BAM_GRADING, FLAG_BAM_REPORTS } from "services/feature/FeatureFlagService";
import { deriveTenantUuid } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { logger } from "utils/logging";

import testIds from "testIds.json";

enum Tab {
    OVERVIEW = "overview",
    INVENTORY = "inventory",
    MANIFEST = "manifest",
    RISK = "risk",
    VALUE = "value",
    REPORTS = "reports",
}

export interface Props {
    viewDetails?: AssetCustomView;
}

const AssetCustomViewPage: React.FC<Props> = (props) => {
    const { t } = useTranslation();
    const [assets, setAssets] = useState<Asset[]>([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [totalAssetCount, setTotalAssetCount] = useState(0);
    const [activeFilters, setActiveFilters] = React.useState<AssetFilter[]>([]);
    const [riskFilter, setRiskFilter] = React.useState<AssetFilters | undefined>(undefined);
    const [isUploadAssetsModalOpen, setIsUploadAssetsModalOpen] = useState(false);
    let viewTitle = t("AssetManagement.assetInventory");
    let viewDetails: AssetCustomView = {
        uuid: "",
        name: t("AssetManagement.assetInventory"),
        columns: DEFAULT_COLUMN_KEYS,
        folder: false,
        childrenViews: [],
    };

    if (props?.viewDetails) {
        viewTitle = props.viewDetails.name;
        viewDetails = props.viewDetails;
    }

    const hasFeatureLicense = userSessionService.hasFeatureLicense("FEATURE_ASSET_MANAGEMENT");
    const hasViewAssetsAuth = userSessionService.userHasAllAuthorities([AUTH_ASSET_VIEW]);
    const hasFeatureFlagEnabled = useFeature(FLAG_ASSET_MANAGEMENT);
    const hasValuesFeatureFlagEnabled = useFeature(FLAG_BAM_GRADING);
    const hasReportsFeatureFlagEnabled = useFeature(FLAG_BAM_REPORTS);
    const [pendingExportIds, setPendingExportIds] = React.useState<string[] | undefined>(undefined);
    const [pendingExportColumns, setPendingExportColumns] = useState<string[] | undefined>(undefined);
    const [activeTab, setActiveTab] = useState<Tab>(Tab.OVERVIEW);
    const hasManifestViewAuth = userSessionService.userHasAllAuthorities([AUTH_ASSET_MANIFEST_VIEW]);
    const { setSubPage } = useSubPage();
    const [currentVisibleColumns, setCurrentVisibleColumns] = useState<string[]>(viewDetails.columns);
    const [manifest, setManifest] = useState<Manifest>();
    const [isRemoveFromGroupModalOpen, setIsRemoveFromGroupModalOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= Breakpoint.MD);
    const [isRemoveFromGroupErrorModalOpen, setIsRemoveFromGroupErrorModalOpen] = useState(false);
    const assetUuidsToRemoveRef = useRef<string[]>([]);
    const customViewUuid = props.viewDetails?.uuid;
    const shouldFetchManifest = customViewUuid !== undefined && hasManifestViewAuth;

    const loadManifest = React.useCallback(
        (abortController?: AbortController) => {
            if (!shouldFetchManifest || customViewUuid === undefined) {
                setManifest(undefined);
                return;
            }
            assetViewService
                .fetchManifest(customViewUuid, abortController)
                .then((data) => {
                    if (!abortController?.signal.aborted) {
                        setManifest(data);
                    }
                })
                .catch((error: Error & { status?: number }) => {
                    if (abortController?.signal.aborted) {
                        return;
                    }
                    setManifest(undefined);
                    logger.error("Failed to fetch manifest for custom view:", error);
                });
        },
        [customViewUuid, shouldFetchManifest]
    );

    React.useEffect(() => {
        const abortController = new AbortController();
        loadManifest(abortController);
        return () => abortController.abort();
    }, [loadManifest]);

    React.useEffect(() => {
        const mediaQuery = window.matchMedia(`(max-width: ${Breakpoint.MD}px)`);
        const handleChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
    }, []);

    if (!hasFeatureLicense || !hasViewAssetsAuth || !hasFeatureFlagEnabled) {
        return (
            <ApplicationLayout
                viewTitle={viewTitle}
                view={<div className="unauthorized-message">{t("AssetManagement.viewAssetsPermissionRequired")}</div>}
                mainRoute={ASSET_MANAGEMENT_ROUTE}
            />
        );
    }

    const handleAssetsChange = (newAssets: Asset[]) => {
        setAssets(newAssets);
    };

    const handleRemoveFromGroup = (assetUuids: string[]) => {
        if (!props.viewDetails?.uuid) {
            return;
        }
        assetUuidsToRemoveRef.current = assetUuids;
        setIsRemoveFromGroupModalOpen(true);
    };

    const handleConfirmRemoveFromGroup = () => {
        const groupUuid = props.viewDetails?.uuid;
        if (!groupUuid) {
            return;
        }
        setIsRemoveFromGroupModalOpen(false);
        const abortController = new AbortController();
        assetViewService
            .removeAssetsFromGroup(groupUuid, assetUuidsToRemoveRef.current, abortController)
            .then(() => {
                showToast(t("AssetManagement.removeFromGroupModal.successMessage"), "success");
                setRefreshKey((prev) => prev + 1);
            })
            .catch((error) => {
                logger.error("Failed to remove assets from group.", error);
                setIsRemoveFromGroupErrorModalOpen(true);
            });
    };

    const onViewDelete = () => {
        window.location.replace(ASSET_OVERVIEW_ROUTE.path);
    };

    const kebabMenu = props?.viewDetails && (
        <AssetViewKebabMenu
            name={viewTitle}
            viewDetails={props.viewDetails || viewDetails}
            onViewDelete={onViewDelete}
            kebabSize={"Md"}
        />
    );

    const buildExportAssetsView = (sendEmail?: boolean) => (
        <ExportAssetsView
            assets={assets}
            columns={pendingExportColumns ?? currentVisibleColumns}
            totalAssetCount={totalAssetCount}
            filters={
                props.viewDetails?.filters ? mapCustomViewFiltersToApiFilters(props.viewDetails.filters) : activeFilters
            }
            selectedAssetIds={sendEmail ? undefined : pendingExportIds}
            onExportDone={() => {
                setPendingExportIds(undefined);
                setPendingExportColumns(undefined);
                setRefreshKey((prev) => prev + 1);
            }}
            customViewUuid={props.viewDetails?.uuid}
            sendEmail={sendEmail}
            manifestExists={manifest !== undefined}
            className={style.viewActionsButtons}
        />
    );

    const createViewActions = () => {
        return (
            <>
                {hasFeatureLicense &&
                    userSessionService.userHasAllAuthorities([AUTH_ASSET_GRADING]) &&
                    hasValuesFeatureFlagEnabled &&
                    isMobile && (
                        <Button
                            icon={<External />}
                            onClick={() => {
                                const url = buildCosmeticGradingUrl(deriveTenantUuid());
                                window.open(url, "_blank");
                            }}
                            variant="Secondary"
                            testId={testIds.workArea.asset.cosmeticGradingButton}
                            className={style.viewActionsButtons}
                        >
                            {t("AssetManagement.cosmeticGrading.buttonText")}
                        </Button>
                    )}
                {hasFeatureLicense &&
                    userSessionService.userHasAnyAuthority([AUTH_ASSET_VIEW, AUTH_CUSTOM_ASSET_VIEW_VIEW]) &&
                    buildExportAssetsView(true)}
                {hasFeatureLicense &&
                    userSessionService.userHasAnyAuthority([AUTH_ASSET_VIEW, AUTH_CUSTOM_ASSET_VIEW_VIEW]) &&
                    buildExportAssetsView()}
                {hasFeatureLicense && userSessionService.userHasAllAuthorities([AUTH_ASSET_CREATE]) && (
                    <Button
                        icon={<Upload />}
                        onClick={() => setIsUploadAssetsModalOpen(true)}
                        variant="Secondary"
                        testId={testIds.workArea.asset.uploadAssetsButton}
                        className={style.viewActionsButtons}
                    >
                        {t("AssetManagement.upload.buttonText")}
                    </Button>
                )}
                {hasFeatureLicense && userSessionService.userHasAllAuthorities([AUTH_ASSET_CREATE]) && (
                    <Button
                        testId={testIds.workArea.asset.createAssetButton}
                        variant="Primary"
                        onClick={() =>
                            setSubPage(
                                <ManageAssetView
                                    groupUuid={props.viewDetails?.uuid}
                                    onCreateDone={() => setRefreshKey((previousState) => previousState + 1)}
                                />
                            )
                        }
                        icon={<Add />}
                        className={style.viewActionsButtons}
                    >
                        {t("AssetManagement.createAsset")}
                    </Button>
                )}
                <UploadAssetsModal
                    isOpen={isUploadAssetsModalOpen}
                    onClose={() => setIsUploadAssetsModalOpen(false)}
                    groupUuid={props.viewDetails?.uuid}
                />
            </>
        );
    };

    const buildAssetSearchRequest = (): AssetSearchRequest => {
        const viewFilters = mapCustomViewFiltersToApiFilters(props.viewDetails?.filters ?? []);
        const request: AssetSearchRequest = {
            paths: [],
            filters: [...viewFilters],
            ...(props.viewDetails?.uuid && { groupUuid: props.viewDetails.uuid }),
        };
        const totalFilters = request.filters!.length;
        if (totalFilters > 1) {
            const viewLogic = buildViewFilterLogic(props.viewDetails?.filters ?? [], 0);
            if (viewLogic) {
                request.filterLogic = viewLogic;
            }
        }
        return request;
    };

    const handleTabClick = (tab: Tab) => {
        setActiveTab(tab);
        setRiskFilter(undefined);
    };

    const handleViewDevices = (riskLevel?: RiskLevel) => {
        if (riskLevel) {
            setRiskFilter({ riskLevel: [riskLevel] });
        }
        setActiveTab(Tab.INVENTORY);
    };

    const renderAssetTable = (testId?: string) => {
        return (
            <AssetTable
                columns={viewDetails.columns}
                filters={riskFilter}
                startDate={viewDetails.startDate}
                endDate={viewDetails.endDate}
                viewId={viewDetails.uuid || undefined}
                viewFilters={viewDetails.filters}
                onAssetsChange={handleAssetsChange}
                refreshKey={refreshKey}
                onTotalCountChange={(count) => setTotalAssetCount(count)}
                onFiltersChange={(filters) => setActiveFilters(filters)}
                onExportSelected={(ids, columnKeys) => {
                    setPendingExportIds(ids);
                    setPendingExportColumns(columnKeys);
                }}
                onAssetEdit={() => setRefreshKey((prev) => prev + 1)}
                onVisibleColumnsChange={(cols) => setCurrentVisibleColumns(cols)}
                toShowRemoveFromGroup={props.viewDetails !== undefined}
                onRemoveFromGroup={handleRemoveFromGroup}
                testId={testId}
            />
        );
    };
    const renderView = () => {
        const assetSearchRequest = buildAssetSearchRequest();

        if (activeTab === Tab.OVERVIEW) {
            return (
                <OverviewTab
                    onViewDevices={handleViewDevices}
                    assetSearchRequest={assetSearchRequest}
                    viewDetails={props.viewDetails}
                    refreshKey={refreshKey}
                />
            );
        } else if (activeTab === Tab.INVENTORY) {
            return renderAssetTable(testIds.workArea.asset.customView.inventory.itself);
        } else if (activeTab === Tab.MANIFEST) {
            return (
                <ManifestTab
                    customViewUuid={viewDetails.uuid}
                    viewName={viewTitle}
                    onCancel={() => setActiveTab(Tab.OVERVIEW)}
                    readOnly={true}
                    manifest={manifest}
                    onManifestRefresh={() => loadManifest()}
                />
            );
        } else if (activeTab === Tab.RISK) {
            return (
                <RiskTab
                    onViewDevices={handleViewDevices}
                    assetSearchRequest={assetSearchRequest}
                    refreshKey={refreshKey}
                />
            );
        } else if (activeTab === Tab.VALUE) {
            return (
                <ValueTab
                    assetSearchRequest={assetSearchRequest}
                    onResolveRisks={() => handleTabClick(Tab.RISK)}
                    refreshKey={refreshKey}
                />
            );
        } else if (activeTab === Tab.REPORTS) {
            return <ReportsTab />;
        } else {
            return renderAssetTable();
        }
    };

    const view = props.viewDetails ? (
        <>
            <Box paddingBottom={"Md"}>
                <TabMenu>
                    <TabMenuItem
                        key={0}
                        isActive={activeTab === Tab.OVERVIEW}
                        onClick={() => handleTabClick(Tab.OVERVIEW)}
                        dataType="OVERVIEW"
                    >
                        {t("AssetManagement.tab.overview")}
                    </TabMenuItem>
                    <TabMenuItem
                        key={1}
                        isActive={activeTab === Tab.INVENTORY}
                        onClick={() => handleTabClick(Tab.INVENTORY)}
                        dataType="INVENTORY"
                    >
                        {t("AssetManagement.tab.inventory")}
                    </TabMenuItem>

                    {hasManifestViewAuth && (
                        <TabMenuItem
                            key={2}
                            isActive={activeTab === Tab.MANIFEST}
                            onClick={() => handleTabClick(Tab.MANIFEST)}
                            dataType="MANIFEST"
                        >
                            {t("AssetManagement.tab.manifest")}
                        </TabMenuItem>
                    )}
                    <TabMenuItem
                        key={3}
                        isActive={activeTab === Tab.RISK}
                        onClick={() => handleTabClick(Tab.RISK)}
                        dataType="RISK"
                    >
                        {t("AssetManagement.tab.risk")}
                    </TabMenuItem>
                    {hasValuesFeatureFlagEnabled && (
                        <TabMenuItem
                            key={4}
                            isActive={activeTab === Tab.VALUE}
                            onClick={() => handleTabClick(Tab.VALUE)}
                            dataType="VALUE"
                        >
                            {t("Common.value")}
                        </TabMenuItem>
                    )}
                    {hasReportsFeatureFlagEnabled && (
                        <TabMenuItem
                            key={5}
                            isActive={activeTab === Tab.REPORTS}
                            onClick={() => handleTabClick(Tab.REPORTS)}
                            dataType="REPORTS"
                        >
                            {t("AssetManagement.tab.reports")}
                        </TabMenuItem>
                    )}
                </TabMenu>
            </Box>
            {renderView()}
        </>
    ) : (
        renderAssetTable()
    );
    return (
        <>
            <ApplicationLayout
                viewTitle={viewTitle}
                view={view}
                mainRoute={ASSET_MANAGEMENT_ROUTE}
                kebabMenu={kebabMenu}
                viewActions={createViewActions()}
                enableRefresh
                onHandleRefresh={() => {
                    setRefreshKey((prev) => prev + 1);
                    if (activeTab === Tab.MANIFEST) {
                        loadManifest();
                    }
                }}
                viewActionsClassName={style.viewActions}
            />

            <Modal
                title={t("AssetManagement.removeFromGroup")}
                isOpen={isRemoveFromGroupModalOpen}
                hideModal={() => setIsRemoveFromGroupModalOpen(false)}
                buttons={
                    <>
                        <Button variant="Primary" onClick={handleConfirmRemoveFromGroup}>
                            {t("AssetManagement.removeFromGroup")}
                        </Button>
                        <Button variant="Secondary" onClick={() => setIsRemoveFromGroupModalOpen(false)}>
                            {t("Common.cancel")}
                        </Button>
                    </>
                }
            >
                <TextBlock>{t("AssetManagement.removeFromGroupModal.confirmationMessage")}</TextBlock>
            </Modal>

            <AlertModal
                title={t("Common.failure")}
                variant={"Error"}
                isOpen={isRemoveFromGroupErrorModalOpen}
                hideModal={() => setIsRemoveFromGroupErrorModalOpen(false)}
                buttons={
                    <Button variant="Primary" onClick={() => setIsRemoveFromGroupErrorModalOpen(false)}>
                        {t("Common.ok")}
                    </Button>
                }
            >
                <TextBlock>{t("AssetManagement.removeFromGroupModal.failureMessage")}</TextBlock>
            </AlertModal>
        </>
    );
};

export default AssetCustomViewPage;
